import { get, post, put, Delete } from "@/services/api";
import store from "@/store";

// Speedcast users must identify themselves via an email query param on every endpoint.
function speedcastConfig() {
  if (!store.getters.isSpeedcast) return undefined;
  const params = new URLSearchParams();
  params.set("email", store.getters.getUserIdentifier);
  return { params };
}

// GET /myapi/dashboards
export function fetchDashboards() {
  return get("myapi/dashboards", speedcastConfig()).then((res) => res.data);
}

// GET /myapi/dashboards/{id}
export function fetchDashboardById(id) {
  return get(`myapi/dashboards/${id}`, speedcastConfig()).then((res) => res.data);
}

/**
 * @typedef {Object} DashboardWidgetDraft
 * @property {string} widgetId
 * @property {string|number|null} deviceId
 * @property {number} x
 * @property {number} y
 * @property {number} w
 * @property {number} h
 */

/**
 * @param {DashboardWidgetDraft[]} widgets
 */
function dashboardWidgetPayload(widgets = []) {
  return widgets.map(({ widgetId, deviceId, x, y, w, h }) => ({
    widgetId,
    deviceId,
    x,
    y,
    w,
    h,
  }));
}

// POST /myapi/dashboards
/**
 * @param {{
 *   title: string,
 *   companyId: string,
 *   isDateFilterEnabled?: boolean,
 *   isTitleVisible?: boolean,
 *   isEditButtonVisible?: boolean,
 *   isExportButtonVisible?: boolean,
 *   widgets?: DashboardWidgetDraft[],
 * }} params
 */
export function createDashboard({
  title,
  companyId,
  isDateFilterEnabled = false,
  isTitleVisible = true,
  isEditButtonVisible = true,
  isExportButtonVisible = true,
  widgets = [],
}) {
  const body = {
    name: title,
    companyId,
    isDateFilterEnabled,
    isTitleVisible,
    isEditButtonVisible,
    isExportButtonVisible,
    ...(widgets.length ? { widgets: dashboardWidgetPayload(widgets) } : {}),
  };
  return post("myapi/dashboards", body, speedcastConfig()).then((res) => res.data);
}

// PUT /myapi/dashboards/{id}
export function updateDashboard(id, data = {}) {
  const { title, isDateFilterEnabled, isTitleVisible, isEditButtonVisible, isExportButtonVisible } = data;
  const body = {};
  if (title !== undefined) body.name = title;
  if (isDateFilterEnabled !== undefined) body.isDateFilterEnabled = isDateFilterEnabled;
  if (isTitleVisible !== undefined) body.isTitleVisible = isTitleVisible;
  if (isEditButtonVisible !== undefined) body.isEditButtonVisible = isEditButtonVisible;
  if (isExportButtonVisible !== undefined) body.isExportButtonVisible = isExportButtonVisible;
  return put(`myapi/dashboards/${id}`, body, speedcastConfig()).then((res) => res.data);
}

// DELETE /myapi/dashboards/{id}
export function deleteDashboard(id) {
  return Delete(`myapi/dashboards/${id}`, speedcastConfig());
}

// POST /myapi/{companyId}/dashboards/delete
export function deleteDashboards(companyId, ids) {
  return post(`myapi/${companyId}/dashboards/delete`, { dashboardIds: ids }, speedcastConfig()).then((res) => res.data);
}

// PUT /myapi/dashboards/{id}/widgets
async function saveDashboardWidgets(id, widgets, retries = 3) {
  const trimmed = dashboardWidgetPayload(widgets);
  try {
    const res = await put(`myapi/dashboards/${id}/widgets`, { widgets: trimmed }, speedcastConfig());
    return res.data;
  } catch (err) {
    const code = err?.response?.data?.error?.error?.errno;
    if (code === 1213 && retries > 0) {
      await new Promise((r) => setTimeout(r, 100));
      return saveDashboardWidgets(id, widgets, retries - 1);
    }
    throw err;
  }
}

export function updateDashboardOrder(companyId, orderedIds) {
  return post(`myapi/${companyId}/dashboards/order`, { dashboardIds: orderedIds }, speedcastConfig()).then(
    (res) => res.data
  );
}

export { saveDashboardWidgets };
